import { useLogout } from "@/common/hooks/use-logout";
import { SIDEBAR_KEYBOARD_SHORTCUT, useSidebar } from "@/components/ui/sidebar";
import { THEME_MODE_OPTIONS } from "@/modules/user-settings/_constants/themes";
import { userConfigStore } from "@/store/user-config-store";
import { LogOutIcon, PanelLeftIcon, SettingsIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getConfettiSeason } from "../_helpers/confetti-season";
import { fireConfetti } from "../_helpers/fire-confetti";
import { getModifierKeyLabel } from "../_helpers/keyboard";
import type { PaletteCommand } from "../types";

export function useGeneralCommands(): PaletteCommand[] {
  const { t } = useTranslation();
  const { toggleSidebar } = useSidebar();
  const logout = useLogout();
  const theme = userConfigStore((state) => state.theme);
  const setTheme = userConfigStore((state) => state.setTheme);

  return useMemo(() => {
    const season = getConfettiSeason();
    const SeasonIcon = season.icon;

    return [
      {
        id: "toggle-sidebar",
        title: t("ToggleSidebar"),
        icon: <PanelLeftIcon />,
        shortcut: [getModifierKeyLabel(t), SIDEBAR_KEYBOARD_SHORTCUT.toUpperCase()],
        perform: toggleSidebar,
      },
      { id: "settings", title: t("user-settings:Settings"), icon: <SettingsIcon />, to: "/settings" },
      ...THEME_MODE_OPTIONS.filter((option) => option.theme !== theme).map(
        ({ theme: mode, labelKey, Icon }): PaletteCommand => ({
          id: `theme-${mode}`,
          title: t("CommandPaletteThemeMode", { mode: t(labelKey) }),
          icon: <Icon />,
          keywords: ["theme", "design", t("user-settings:Appearance")],
          perform: () => setTheme(mode),
        }),
      ),
      {
        id: "logout",
        title: t("Logout"),
        icon: <LogOutIcon />,
        keywords: ["logout", "sign out"],
        perform: () => void logout(),
      },
      {
        id: "confetti",
        title: t(season.labelKey),
        icon: <SeasonIcon />,
        keywords: ["tada", "confetti", "konfetti", "party", "celebrate", ...season.keywords],
        perform: () => void fireConfetti(season),
      },
    ];
  }, [t, toggleSidebar, logout, theme, setTheme]);
}
